import { useCallback, useEffect } from 'react';
import { useUIStore, useWatchlistStore } from '../stores';
import type { SelectedCompany, SelectedIndustry } from '../types';

export const useMobileNavigation = (isMobile: boolean) => {
  // UI Store
  const {
    showMobilePanel,
    mobileBottomNavTab,
    lastActiveTab,
    savedScrollPositions,
    mobileViewMode,
    setShowMobilePanel,
    setMobileBottomNavTab,
    saveScrollPosition,
    switchToContent,
    switchToNavigation,
  } = useUIStore();

  // Watchlist Store
  const {
    selectedCompany,
    selectedIndustry,
    setSelectedCompany,
    setSelectedIndustry,
  } = useWatchlistStore();

  // 保存滚动位置
  const handleSaveScrollPosition = useCallback((tabKey: string) => {
    if (isMobile) {
      const scrollY = window.scrollY;
      saveScrollPosition(tabKey, scrollY);
    }
  }, [isMobile, saveScrollPosition]);

  // 恢复滚动位置
  const restoreScrollPosition = useCallback((tabKey: string) => {
    if (isMobile) {
      const savedPosition = savedScrollPositions[tabKey];
      if (savedPosition !== undefined) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: savedPosition,
            behavior: 'auto',
          });
        });
      }
    }
  }, [isMobile, savedScrollPositions]);

  // 处理公司选择
  const handleCompanySelect = useCallback((company: SelectedCompany) => {
    setSelectedCompany(company);
    setSelectedIndustry(null);
    
    if (isMobile) {
      switchToContent(mobileBottomNavTab);
    }
  }, [setSelectedCompany, setSelectedIndustry, isMobile, switchToContent, mobileBottomNavTab]);

  // 处理行业选择
  const handleIndustrySelect = useCallback((industry: SelectedIndustry) => {
    setSelectedIndustry(industry);
    setSelectedCompany(null);
    
    if (isMobile) {
      switchToContent(mobileBottomNavTab);
    }
  }, [setSelectedIndustry, setSelectedCompany, isMobile, switchToContent, mobileBottomNavTab]);

  // 处理底部导航变化
  const handleMobileBottomNavChange = useCallback((tab: 'home' | 'companies' | 'industries') => {
    // 先保存当前滚动位置
    handleSaveScrollPosition(mobileBottomNavTab);
    
    // 切换到导航模式
    switchToNavigation(tab);
    
    // 根据标签页类型清除不相关的选择状态
    const currentlyShowingCompany = selectedCompany !== null;
    const currentlyShowingIndustry = selectedIndustry !== null;
    
    if (tab === 'home') {
      // 主页显示Dashboard，清除所有选择
      if (currentlyShowingCompany || currentlyShowingIndustry) {
        setSelectedCompany(null);
        setSelectedIndustry(null);
      }
    } else if (tab === 'companies') {
      // 公司标签，只清除行业选择
      if (currentlyShowingIndustry) {
        setSelectedIndustry(null);
      }
    } else if (tab === 'industries') {
      // 行业标签，只清除公司选择
      if (currentlyShowingCompany) {
        setSelectedCompany(null);
      }
    }
    
    // 延迟恢复新标签页的滚动位置
    setTimeout(() => {
      restoreScrollPosition(tab);
    }, 50);
  }, [
    handleSaveScrollPosition,
    mobileBottomNavTab,
    switchToNavigation,
    selectedCompany,
    selectedIndustry,
    setSelectedCompany,
    setSelectedIndustry,
    restoreScrollPosition
  ]);

  // 处理返回列表
  const handleBackToList = useCallback(() => {
    setShowMobilePanel('list');
    setMobileBottomNavTab(lastActiveTab);
    
    // 延迟恢复滚动位置，确保组件已经重新渲染
    setTimeout(() => {
      restoreScrollPosition(lastActiveTab);
    }, 100);
  }, [setShowMobilePanel, setMobileBottomNavTab, lastActiveTab, restoreScrollPosition]);

  // 处理Logo点击
  const handleLogoClick = useCallback(() => {
    setSelectedCompany(null);
    setSelectedIndustry(null);
    
    if (isMobile) {
      switchToNavigation('home');
    }
  }, [setSelectedCompany, setSelectedIndustry, isMobile, switchToNavigation]);

  // 页面卸载时保存滚动位置
  useEffect(() => {
    if (!isMobile) return;

    let scrollTimer: NodeJS.Timeout;

    const handleBeforeUnload = () => {
      handleSaveScrollPosition(mobileBottomNavTab);
    };

    const handleScroll = () => {
      if (showMobilePanel === 'list') {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          handleSaveScrollPosition(mobileBottomNavTab);
        }, 150);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [isMobile, mobileBottomNavTab, showMobilePanel, handleSaveScrollPosition]);

  return {
    // State
    showMobilePanel,
    mobileBottomNavTab,
    lastActiveTab,
    mobileViewMode,
    
    // Handlers
    handleCompanySelect,
    handleIndustrySelect,
    handleMobileBottomNavChange,
    handleBackToList,
    handleLogoClick,
    handleSaveScrollPosition,
    restoreScrollPosition,
  };
};